import React from 'react';
import { Link } from 'react-router-dom';

/** Prefer WebP when a sibling .webp exists; keep original as fallback. */
const toWebpSrc = (src) => {
  if (!src) return null;
  return src.replace(/\.(jpe?g|png)(\?.*)?$/i, '.webp$2');
};

const HeroPicture = ({ src, className, style }) => {
  if (!src) return null;
  const webp = toWebpSrc(src);
  return (
    <picture className="absolute inset-0 block w-full h-full">
      {webp && webp !== src && (
        <source srcSet={webp} type="image/webp" />
      )}
      <img
        src={src}
        alt=""
        className={className}
        style={style}
      />
    </picture>
  );
};

const PageHero = ({
  title,
  subtitle,
  breadcrumbs = [],
  backgroundImage,
  variant = 'default',
  children,
  imagePosition = 'center 30%',
  childrenPosition = 'image-right',
}) => {
  return (
    <>
      <section
        className="relative overflow-hidden min-h-[220px] sm:min-h-[260px] md:min-h-[300px]"
      >
        {variant === 'split' ? (
          <>
            {/* Navy background fills everything */}
            <div className="absolute inset-0 bg-ifys-blue" />

            {/* Image only on the right side, starting near the slant */}
            <div
              className="absolute top-0 bottom-0 right-0"
              style={{ left: '42%' }}
            >
              <HeroPicture
                src={backgroundImage}
                className="w-full h-full object-cover"
                style={{ objectPosition: imagePosition }}
              />
            </div>

            {/* Navy diagonal that overlaps the image edge */}
            <div
              className="absolute inset-0 bg-ifys-blue"
              style={{ clipPath: 'polygon(0 0, 48% 0, 44% 100%, 0 100%)' }}
            />

            {/* Title is horizontally + vertically centered within the navy panel.
                The navy diagonal runs from 48% at the top to 44% at the bottom,
                so the panel's visual center is ~23%. We size the container to
                44% (the bottom edge of the slant) so the text never bleeds
                into the slant on tall screens, while sitting visually centered. */}
            <div
              className="absolute inset-y-0 left-0 z-10 flex items-center justify-center px-4 sm:px-6"
              style={{ width: '44%' }}
            >
              <div className="text-center w-full">
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white tracking-tight leading-tight">
                  {title}
                </h1>
                {subtitle && (
                  <p className="mt-2 sm:mt-3 text-white/85 text-xs sm:text-sm md:text-base leading-snug">
                    {subtitle}
                  </p>
                )}
              </div>
            </div>

            {/* Children render as circular badges floating over the photo.
                'image-right' (default) — anchored to the right side of the photo.
                'image-left'  — anchored just over the left edge of the photo. */}
            {children && childrenPosition === 'image-right' && (
              <div className="hidden md:flex absolute inset-y-0 right-4 lg:right-10 xl:right-16 items-center gap-3 lg:gap-4 z-20 pointer-events-none">
                <div className="flex items-center gap-3 lg:gap-4 pointer-events-auto">
                  {children}
                </div>
              </div>
            )}
            {children && childrenPosition === 'image-left' && (
              <div
                className="hidden md:flex absolute inset-y-0 items-center z-20 pointer-events-none"
                style={{ left: '48%', right: 0 }}
              >
                {/* Entire button must sit in the photo zone — never over the navy panel.
                    The slant runs 48% (top) → 44% (bottom), so the container starts at 48%. */}
                <div className="pl-3 lg:pl-5 pointer-events-auto">{children}</div>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="absolute inset-0 bg-ifys-blue">
              <HeroPicture
                src={backgroundImage}
                className="w-full h-full object-cover opacity-60"
                style={{ objectPosition: 'center 30%' }}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-ifys-blue/80 via-ifys-blue/50 to-transparent" />
            </div>

            <div className="relative z-10 h-full flex items-center justify-center py-6 sm:py-8 px-4 sm:px-6 lg:px-8">
              <div className="max-w-7xl w-full mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
                <div className="flex-1 text-center">
                  <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white tracking-tight leading-tight">
                    {title}
                  </h1>
                  {subtitle && (
                    <p className="mt-2 sm:mt-3 text-white/85 text-xs sm:text-sm md:text-base leading-snug max-w-2xl mx-auto">
                      {subtitle}
                    </p>
                  )}
                </div>
                {children && (
                  <div className="flex flex-wrap gap-3 sm:gap-4">
                    {children}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </section>

      {breadcrumbs.length > 0 && (
        <nav
          aria-label="Breadcrumb"
          className="bg-gray-100 border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-3"
        >
          <ol className="max-w-7xl mx-auto flex flex-wrap items-center gap-1 text-sm text-gray-600">
            {breadcrumbs.map((crumb, i) => (
              <li key={crumb.label} className="flex items-center gap-1">
                {i > 0 && <span className="text-gray-400">/</span>}
                {crumb.to ? (
                  <Link
                    to={crumb.to}
                    className="hover:text-ifys-blue transition-colors duration-150"
                  >
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="text-gray-900 font-medium">{crumb.label}</span>
                )}
              </li>
            ))}
          </ol>
        </nav>
      )}
    </>
  );
};

export default PageHero;
